import { useEffect, useState } from "react";
import { Eye, EyeSlash } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";

// Per-exchange display rules
const EXCHANGE_META = {
  simulator: { label: "Simulator", note: "Local synthetic order book — no creds required", sig: "n/a" },
  binance:   { label: "Binance",   note: "IP binding optional but recommended · Ed25519 supported", sig: "HmacSHA256 / Ed25519" },
  lbank:     { label: "LBank",     note: "IP whitelist MANDATORY · up to 20 IPs · Permanent validity", sig: "HmacSHA256 V2" },
  coinstore: { label: "Coinstore", note: "IP whitelist MANDATORY · max 5 IPs · custom REST client", sig: "HmacSHA256" },
  mexc:      { label: "MEXC",      note: "Stock CCXT 4.x", sig: "HmacSHA256" },
  bybit:     { label: "Bybit",     note: "Stock CCXT 4.x · may require API passphrase on some accounts", sig: "HmacSHA256" },
  gate:      { label: "Gate.io",   note: "Stock CCXT 4.x", sig: "HmacSHA512" },
};

const TOP_FIELD_GROUPS = [
  {
    title: "Live trading safety",
    fields: [
      { key: "live_trading_enabled", type: "boolean", note: "When FALSE the bot only places dry-run orders even on real exchanges" },
      { key: "testnet", type: "boolean" },
      { key: "operating_mode", type: "select", options: ["market_maker", "buy_only", "sell_only"] },
      { key: "symbol", type: "text", note: "global default pair · per-exchange override below" },
    ],
  },
  {
    // Phase 4 (Feb 2026) — dedicated Price Guard section. Bilingual labels
    // and per-field notes are pulled from i18n at render time.
    title: "__PRICE_GUARD__",   // marker replaced by t('pgSectionTitle')
    priceGuard: true,
    fields: [
      { key: "price_guard_enabled", type: "boolean" },
      { key: "price_guard_dry_run", type: "boolean" },
      { key: "daily_price_limit_pct", type: "number", step: "0.1" },
      { key: "adjustment_rate_pct_per_minute", type: "number", step: "0.01" },
      { key: "safety_buffer_pct", type: "number", step: "0.05" },
      { key: "daily_open_source", type: "select", options: ["exchange_ticker", "daily_kline", "manual"] },
      { key: "daily_open_timezone", type: "text" },
      { key: "daily_open_reset_time", type: "text" },
      { key: "daily_open_max_age_hours", type: "number", step: "1" },
      { key: "daily_open_manual_price", type: "number", step: "0.00000001" },
      { key: "price_guard_exchanges", type: "text" },
      { key: "price_guard_pairs", type: "text" },
      { key: "cancel_repost_delay_ms", type: "number", step: "10" },
      { key: "exchange_meta_cache_ttl_s", type: "number", step: "30" },
      { key: "intention_registry_enabled", type: "boolean" },
      { key: "intention_window_sec", type: "number", step: "1" },
      { key: "hysteresis_persist_enabled", type: "boolean" },
    ],
  },
  {
    title: "Compliance — NG v1.1 (hardcap, depth, volume)",
    fields: [
      { key: "spread_hard_cap", type: "number", step: "0.01", note: "% — must stay below exchange rule (1.5)" },
      { key: "spread_min_pct", type: "number", step: "0.05", note: "% — minimum spread to place" },
      { key: "spread_max_pct", type: "number", step: "0.05", note: "% — maximum spread allowed before tightening" },
      { key: "min_depth_each_side_usd", type: "number", step: "1", note: "$ — min USD depth in each side of band" },
      { key: "depth_band_pct", type: "number", step: "0.1", note: "% — ± band around mid for depth check" },
      { key: "volume_target_daily_usd", type: "number", step: "10", note: "$ — 24h volume floor" },
      { key: "volume_alert_threshold", type: "number", step: "0.05", note: "0.70 = aggressive mode at 70% pace" },
    ],
  },
  {
    title: "Market Maker mode",
    fields: [
      { key: "order_layers", type: "number", step: "1", note: "1–10 bid/ask layers" },
      { key: "order_size_base_usdt", type: "number", step: "5", note: "$ — USDT per layer" },
      { key: "refresh_rate_sec", type: "number", step: "0.1", note: "seconds — main loop cadence" },
      { key: "price_reference", type: "select", options: ["mid", "last", "vwap5m"] },
      { key: "inventory_target_pct", type: "number", step: "1", note: "% — target base token allocation" },
      { key: "inventory_skew_strength", type: "number", step: "0.05", note: "0.0 passive · 1.0 aggressive" },
      { key: "floor_price", type: "number", step: "0.01", note: "$ — buy wall (0 = disabled)" },
      { key: "ceiling_price", type: "number", step: "0.01", note: "$ — sell wall (0 = disabled)" },
    ],
  },
  {
    title: "Buy Only mode",
    fields: [
      { key: "buy_pct_of_ask", type: "number", step: "1", note: "buy at ≤ X% of best ask (75 / 85 / 90 / 100)" },
      { key: "buy_order_size_usdt", type: "number", step: "5", note: "$ — max USDT per sweep order" },
      { key: "buy_sweep_interval_sec", type: "number", step: "0.5", note: "seconds between sweeps" },
      { key: "buy_max_daily_usdt", type: "number", step: "10", note: "$ — daily spend cap (0 = unlimited)" },
      { key: "buy_stop_above_price", type: "number", step: "0.01", note: "$ — stop buying above this price (0 = disabled)" },
    ],
  },
  {
    title: "Sell Only mode",
    fields: [
      { key: "sell_pct_of_bid", type: "number", step: "1", note: "post asks at X% of best bid (e.g. 105)" },
      { key: "sell_order_size_token", type: "number", step: "0.001", note: "base tokens per sell order" },
      { key: "sell_interval_sec", type: "number", step: "0.5", note: "seconds between sells" },
      { key: "sell_min_price", type: "number", step: "0.01", note: "$ — never sell below this price (0 = disabled)" },
      { key: "sell_max_daily_token", type: "number", step: "0.001", note: "tokens — daily sell cap (0 = unlimited)" },
    ],
  },
  {
    title: "Risk Sentinel",
    fields: [
      { key: "max_daily_loss_usd", type: "number", step: "10" },
    ],
  },
  {
    title: "On-Chain & Market Data",
    fields: [
      { key: "onchain_enabled", type: "boolean", note: "Master switch — turn off to disable BSCscan polling" },
      { key: "onchain_contract_address", type: "text", note: "BSC token contract (0x…)" },
      { key: "onchain_chain", type: "text", note: "currently only `bsc` is supported" },
      { key: "onchain_poll_interval_sec", type: "number", step: "10", note: "min 30 seconds" },
      { key: "onchain_bscscan_api_key", type: "sensitive", note: "Optional — free tier works without key" },
      { key: "onchain_exchange_wallets", type: "text", note: "Comma-separated 0x… hot-wallet addresses for whale detection" },
      { key: "cmc_enabled", type: "boolean" },
      { key: "cmc_slug", type: "text", note: "from coinmarketcap.com URL — e.g. `limocoin-swap`" },
      { key: "cmc_api_key", type: "sensitive", note: "Required for CMC API; without it we scrape the public page" },
      { key: "cmc_poll_interval_sec", type: "number", step: "30", note: "min 60 seconds" },
      { key: "cmc_price_weight", type: "number", step: "0.05", note: "0.0–1.0 · weight of CMC price in adjusted mid (1-w = exchange)" },
      { key: "whale_alert_enabled", type: "boolean" },
      { key: "whale_threshold_tokens", type: "number", step: "1000", note: "tokens — transfer ≥ threshold to exchange wallet triggers whale alert" },
      { key: "whale_spread_multiplier", type: "number", step: "0.05", note: "spread widening factor during whale mode" },
      { key: "whale_mode_duration_sec", type: "number", step: "30", note: "seconds — how long whale defensive mode lasts after detection" },
      { key: "floor_wall_multiplier_on_whale", type: "number", step: "0.1", note: "buy-wall qty multiplier during whale mode" },
      { key: "holder_trend_enabled", type: "boolean" },
      { key: "holder_accumulation_threshold", type: "number", step: "1", note: "Δ holders → ACCUMULATION (tighter spread + larger qty)" },
      { key: "holder_distribution_threshold", type: "number", step: "1", note: "Δ holders → DISTRIBUTION (wider spread + warning)" },
      { key: "onchain_volume_ratio_trigger", type: "number", step: "0.5", note: "if onchain_vol / exchange_vol ≥ this → force aggressive mode" },
    ],
  },
  {
    title: "Alerts — Telegram / Email / SMS (Hubtel)",
    fields: [
      { key: "telegram_bot_token", type: "sensitive" },
      { key: "telegram_chat_id", type: "text" },
      { key: "smtp_host", type: "text" },
      { key: "smtp_port", type: "number", step: "1" },
      { key: "smtp_user", type: "text" },
      { key: "smtp_password", type: "sensitive" },
      { key: "smtp_to", type: "text" },
      { key: "hubtel_sms_client_id", type: "sensitive", note: "From Hubtel dashboard → SMS API" },
      { key: "hubtel_sms_client_secret", type: "sensitive" },
      { key: "hubtel_sms_sender_id", type: "text", note: "default: MarqolixNG" },
      { key: "alert_phone_numbers", type: "text", note: "Ghana only · +233XXXXXXXXX · comma-separated" },
      { key: "sms_test_mode", type: "boolean", note: "true = log only, no real Hubtel call" },
      { key: "alert_rate_limit_sec", type: "number", step: "1" },
    ],
  },
  {
    title: "Arbitrage Engine — Cross-Exchange (CEX↔CEX)",
    fields: [
      { key: "arb_enabled", type: "boolean", note: "Master switch · OFF by default" },
      { key: "arb_dry_run", type: "boolean", note: "TRUE = log opportunities only · FALSE = real trades" },
      { key: "arb_kill_switch", type: "boolean", note: "Hard-stop — must be FALSE to start" },
      { key: "arb_allowed_exchanges", type: "text", note: "comma-separated (e.g. lbank,coinstore)" },
      { key: "arb_allowed_pairs", type: "text", note: "comma-separated (e.g. LMCSWAP/USDT)" },
      { key: "arb_min_spread_pct", type: "number", step: "0.05", note: "% — divergence min to trigger" },
      { key: "arb_min_net_profit_usd", type: "number", step: "0.05", note: "$ — required net profit after fees" },
      { key: "arb_max_trade_usdt", type: "number", step: "5", note: "$ — per-cycle USD cap" },
      { key: "arb_max_concurrent_cycles", type: "number", step: "1", note: "1 = serial (safest)" },
      { key: "arb_scan_interval_sec", type: "number", step: "0.5", note: "seconds — book poll cadence" },
      { key: "arb_max_daily_volume_usd", type: "number", step: "100", note: "$ — daily volume cap (0 = unlimited)" },
      { key: "arb_max_daily_capital_usd", type: "number", step: "10", note: "$ — daily capital deployed cap" },
      { key: "arb_max_slippage_pct", type: "number", step: "0.05", note: "% — slippage buffer added to fees" },
      { key: "arb_stop_on_consecutive_errors", type: "number", step: "1", note: "auto-stop after N errors in a row (0=never)" },
      { key: "arb_stop_on_daily_loss_usd", type: "number", step: "1", note: "$ — auto-stop on cumulative loss (0=never)" },
      { key: "arb_per_exchange_fee_pct", type: "text", note: 'JSON map · {"lbank":0.20,"coinstore":0.20}' },
      { key: "arb_alert_on_execution", type: "boolean", note: "Send alert on successful arbitrage execution" },
      { key: "arb_alert_on_error", type: "boolean", note: "Send alert on errors / auto-stop" },
      { key: "arb_alert_min_profit_usd", type: "number", step: "0.10", note: "$ — only ping above this net profit" },
      { key: "arb_alert_on_engine_lifecycle", type: "boolean", note: "Alert on engine start/stop" },
      { key: "arb_daily_profit_target_usd", type: "number", step: "1", note: "$ — daily target alert threshold" },
      { key: "arb_strategies_enabled", type: "text", note: "comma-sep (cross_cex, dex_cex, triangular…)" },
    ],
  },
  {
    title: "Arbitrage Safety Guards (9 configurable)",
    fields: [
      { key: "arb_guard_max_volatility_pct", type: "number", step: "0.1", note: "% — abort if rolling stddev exceeds (0=off)" },
      { key: "arb_guard_volatility_window", type: "number", step: "1", note: "samples used for stddev (default 30)" },
      { key: "arb_guard_max_anomaly_spread_pct", type: "number", step: "0.1", note: "% — skip if spread looks fake (stale book)" },
      { key: "arb_guard_flash_crash_pct", type: "number", step: "0.1", note: "% — abort if mid moved > X in window" },
      { key: "arb_guard_flash_crash_window_sec", type: "number", step: "1", note: "sec — window for flash-crash detection" },
      { key: "arb_guard_min_balance_buffer_pct", type: "number", step: "1", note: "% extra balance required before trading" },
      { key: "arb_guard_max_partial_fill_pct", type: "number", step: "1", note: "% — reject if partial fill > this" },
      { key: "arb_guard_max_price_desync_pct", type: "number", step: "0.05", note: "% — abort if price drifted between fetch and place" },
      { key: "arb_guard_max_inventory_imbalance_pct", type: "number", step: "1", note: "% of max capital allowed as net exposure" },
      { key: "arb_guard_rate_limit_per_minute", type: "number", step: "10", note: "req/min cap per exchange" },
      { key: "arb_guard_rate_limit_buffer_pct", type: "number", step: "1", note: "% — pause at (100-buffer)% of rate budget" },
      { key: "arb_guard_exchange_offline_sec", type: "number", step: "1", note: "sec — exchange considered offline after this gap" },
    ],
  },
  {
    title: "Liquidity Rewards — Third-party maker rebate program",
    fields: [
      { key: "rewards_enabled", type: "boolean", note: "Master switch · OFF by default" },
      { key: "rewards_epoch_days", type: "number", step: "1", note: "Length of a rebate period (days, default 7)" },
      { key: "rewards_rebate_pct_of_fees", type: "number", step: "1", note: "% of exchange fees rebated to LPs (e.g. 30)" },
      { key: "rewards_avg_exchange_fee_pct", type: "number", step: "0.01", note: "Blended exchange maker fee % (e.g. 0.10)" },
      { key: "rewards_max_payout_per_epoch_usd", type: "number", step: "100", note: "$ hard cap on rebate pool per epoch (0=off)" },
      { key: "rewards_currency", type: "text", note: "USDT | USDC | BNB …" },
      { key: "rewards_min_maker_volume_usd", type: "number", step: "10", note: "$ qualifying threshold (informational)" },
    ],
  },
  {
    title: "Price Sanity Guard — refuse orders priced from stale/wrong quotes",
    fields: [
      { key: "price_sanity_max_usd", type: "number", step: "0.0001", note: "USD — reject prices ABOVE this (0=off). For LMCSWAP use ~0.01." },
      { key: "price_sanity_min_usd", type: "number", step: "0.00001", note: "USD — reject prices BELOW this (0=off). For LMCSWAP use ~0.00001." },
    ],
  },
];

const PER_EX_FIELDS = [
  { key: "enabled", type: "boolean" },
  { key: "symbol", type: "text" },
  { key: "testnet", type: "boolean" },
  { key: "api_key", type: "sensitive" },
  { key: "api_secret", type: "sensitive" },
  { key: "passphrase", type: "sensitive", note: "required by some exchanges" },
  { key: "ip_whitelist", type: "text", note: "comma-separated IPs (display + audit only)" },
  { key: "lbank_sign_method", type: "select", options: ["hmac", "rsa", "auto"], note: "LBank only — HmacSHA256 V2 default" },
];

function FieldInput({ field, value, onChange, testid }) {
  const [masked, setMasked] = useState(false);

  if (field.type === "select") {
    return (
      <select data-testid={testid} className="mq-input" value={value ?? ""} onChange={(e) => onChange(e.target.value)}>
        {field.options.map((o) => (<option key={o} value={o}>{o}</option>))}
      </select>
    );
  }
  if (field.type === "boolean") {
    return (
      <select data-testid={testid} className="mq-input" value={String(value)} onChange={(e) => onChange(e.target.value === "true")}>
        <option value="true">true</option>
        <option value="false">false</option>
      </select>
    );
  }
  if (field.type === "sensitive") {
    // Default: value visible. Eye toggle masks the value VISUALLY via
    // CSS only. The input ALWAYS stays type="text" — never type="password"
    // — so Chrome / 1Password / LastPass cannot detect it as a password
    // field and cannot ever auto-fill it with the user's login password.
    //
    // iter20 had a regression: type was flipped to "password" on mask,
    // which re-enabled Chrome's autofill heuristic the moment the
    // operator clicked the eye. iter23 fixes this by keeping the input
    // permanently type="text" + masking the glyphs with -webkit-text-security.
    //
    // All other autofill suppression hints are kept (autoComplete=off,
    // random name attribute, data-lpignore, data-1p-ignore, etc.).
    return (
      <div className="relative">
        <input
          data-testid={testid}
          // NOTE: random suffix on EVERY render is intentional —
          // Chrome's autofill heuristic relies on a STABLE name attribute
          // to match a saved password. Re-randomising breaks that match
          // and prevents the operator's login password from clobbering
          // sensitive config fields (e.g. exchange api_secret). DO NOT useMemo.
          name={`mq-${field.key}-${Math.random().toString(36).slice(2, 8)}`}
          className="mq-input pr-9 font-mono"
          type="text"
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
          data-lpignore="true"
          data-form-type="other"
          data-1p-ignore="true"
          style={
            masked
              ? {
                  // visual masking — works in Chrome, Edge, Safari, FF (text-security).
                  WebkitTextSecurity: "disc",
                  textSecurity: "disc",
                }
              : undefined
          }
        />
        <button
          data-testid={`${testid}-toggle`}
          type="button"
          onClick={() => setMasked((v) => !v)}
          aria-label={masked ? "Show value" : "Hide value"}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--mq-text-2)] hover:text-[var(--mq-green)] transition-colors"
        >
          {masked ? <Eye size={16} weight="bold" /> : <EyeSlash size={16} weight="bold" />}
        </button>
      </div>
    );
  }
  return (
    <input
      data-testid={testid}
      className="mq-input"
      type={field.type}
      step={field.step}
      value={value ?? ""}
      onChange={(e) => onChange(field.type === "number" ? Number(e.target.value) : e.target.value)}
    />
  );
}

export default function ConfigEditor({ onConfigUpdate }) {
  const { t } = useI18n();
  const [cfg, setCfg] = useState(null);
  const [draft, setDraft] = useState({});
  const [touchedTop, setTouchedTop] = useState({});
  const [touchedEx, setTouchedEx] = useState({}); // { binance: { api_key: true } }
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [smsResult, setSmsResult] = useState(null);
  const [smsBusy, setSmsBusy] = useState(false);
  const [expandedEx, setExpandedEx] = useState({});
  const [metaResult, setMetaResult] = useState(null);
  const [metaBusy, setMetaBusy] = useState(false);
  const [metaExchange, setMetaExchange] = useState("");

  const refreshExchangeMeta = async () => {
    if (!metaExchange) return;
    setMetaBusy(true);
    setMetaResult(null);
    try {
      const { data } = await api.post(`/exchanges/${metaExchange}/refresh-meta`, {});
      setMetaResult({ ok: !!data?.ok && !!data?.result?.ok, exchange: metaExchange, ...(data || {}) });
    } catch (e) {
      setMetaResult({ ok: false, exchange: metaExchange, error: e.response?.data?.detail || e.message });
    } finally {
      setMetaBusy(false);
      setTimeout(() => setMetaResult(null), 5000);
    }
  };

  const load = async () => {
    const { data } = await api.get("/config");
    setCfg(data);
    setDraft(data);
    setTouchedTop({});
    setTouchedEx({});
  };
  useEffect(() => { load(); }, []);

  const setTop = (k, v) => {
    setDraft((d) => ({ ...d, [k]: v }));
    setTouchedTop((t) => ({ ...t, [k]: true }));
    setSaved(false);
  };

  const setEx = (exName, k, v) => {
    setDraft((d) => ({
      ...d,
      exchanges: {
        ...d.exchanges,
        [exName]: { ...(d.exchanges?.[exName] || {}), [k]: v },
      },
    }));
    setTouchedEx((t) => ({ ...t, [exName]: { ...(t[exName] || {}), [k]: true } }));
    setSaved(false);
  };

  const dirtyCount = Object.keys(touchedTop).length + Object.values(touchedEx).reduce((acc, m) => acc + Object.keys(m).length, 0);

  const save = async () => {
    setBusy(true);
    try {
      const overrides = {};
      Object.keys(touchedTop).forEach((k) => { overrides[k] = draft[k]; });
      Object.entries(touchedEx).forEach(([exName, fields]) => {
        Object.keys(fields).forEach((fk) => {
          overrides[`exchanges.${exName}.${fk}`] = draft.exchanges?.[exName]?.[fk];
        });
      });
      const { data } = await api.put("/config", { overrides });
      setCfg(data);
      setDraft(data);
      setTouchedTop({});
      setTouchedEx({});
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
      onConfigUpdate?.();
    } finally {
      setBusy(false);
    }
  };

  const testSms = async () => {
    setSmsBusy(true);
    setSmsResult(null);
    try {
      const { data } = await api.post("/sms/test", {});
      setSmsResult(data);
    } catch (e) {
      setSmsResult({ success: false, error: e.response?.data?.detail || e.message });
    } finally {
      setSmsBusy(false);
    }
  };

  if (!cfg) return <div className="mq-panel p-4 text-xs text-[var(--mq-text-3)]">{t("noData")}</div>;

  const exchanges = Object.entries(draft.exchanges || {});
  // Dynamic list of exchange names that support meta refresh — derived
  // directly from the draft config (NO exchange name hardcoded).
  const enabledExchanges = exchanges
    .filter(([, exCfg]) => !!exCfg?.enabled)
    .map(([n]) => n);

  return (
    <div className="mq-panel" data-testid="config-editor">
      <div className="mq-panel-header">
        <span className="mq-heading text-sm font-semibold tracking-wider">{t("config")}</span>
        <div className="flex gap-2 items-center">
          {dirtyCount > 0 && <span className="mq-label" style={{ color: "var(--mq-yellow)" }}>{dirtyCount} unsaved</span>}
          <button data-testid="send-test-sms-button" className="mq-btn" onClick={testSms} disabled={smsBusy}>
            {smsBusy ? "Sending…" : "Send test SMS"}
          </button>
          <button data-testid="config-save-button" className="mq-btn mq-btn-primary" onClick={save} disabled={busy || dirtyCount === 0}>
            {busy ? t("saving") : saved ? t("saved") : t("save")}
          </button>
        </div>
      </div>
      {smsResult && (
        <div data-testid="sms-test-result" className="px-4 py-2 text-xs font-mono border-b"
          style={{ borderColor: "var(--mq-border)", color: smsResult.success ? "var(--mq-green)" : "var(--mq-red)" }}>
          {smsResult.success
            ? `✓ sent · id=${smsResult.id || smsResult.message_id || "?"}${smsResult.test_mode ? " (test mode)" : ""}`
            : `✗ ${smsResult.error || "failed"}`}
        </div>
      )}

      <div className="p-4 space-y-8 max-h-[720px] overflow-y-auto">
        {/* Outbound IP banner — read-only Fixie info for exchange whitelisting */}
        {(cfg.fixie_enabled || cfg.fixie_ip) && (
          <div
            data-testid="fixie-banner"
            className="border rounded p-3 text-xs"
            style={{
              background: "rgba(0, 245, 140, 0.05)",
              borderColor: "var(--mq-border)",
              color: "var(--mq-text-1)",
            }}
          >
            <div className="mq-label mb-1">Outbound IP (for exchange whitelist)</div>
            <div className="font-mono text-sm" data-testid="fixie-ip-value">
              {cfg.fixie_ip || "—"}
            </div>
            <div className="text-[10px] text-[var(--mq-text-3)] mt-1">
              Static IP{cfg.fixie_ip && cfg.fixie_ip.includes(",") ? "s" : ""} routed via Fixie proxy.
              Whitelist on LBank (up to 20) and Coinstore (up to 5).
              {cfg.fixie_enabled ? " · Status: ON" : " · Status: OFF (direct egress)"}
            </div>
          </div>
        )}

        {/* Top-level groups */}
        {TOP_FIELD_GROUPS.map((group) => {
          const isPG = !!group.priceGuard;
          const title = isPG ? t("pgSectionTitle") : group.title;
          const pgNotes = isPG ? (t("pgFieldNotes") || {}) : {};
          return (
            <div
              key={group.title}
              data-testid={isPG ? "price-guard-section" : undefined}
              className={isPG ? "border rounded-md p-3" : ""}
              style={
                isPG
                  ? {
                      background: "rgba(255, 200, 0, 0.04)",
                      borderColor: "var(--mq-border)",
                    }
                  : undefined
              }
            >
              <div className="flex items-center justify-between mb-3 border-b pb-2 gap-3 flex-wrap" style={{ borderColor: "var(--mq-border)" }}>
                <div className="mq-label">{title}</div>
                {isPG && (
                  <div className="flex items-center gap-2">
                    <select
                      data-testid="pg-refresh-meta-select"
                      className="mq-input text-[11px] py-1"
                      value={metaExchange}
                      onChange={(e) => setMetaExchange(e.target.value)}
                    >
                      <option value="">{t("pgRefreshMetaSelect")}</option>
                      {enabledExchanges.map((n) => (
                        <option key={n} value={n}>{n}</option>
                      ))}
                    </select>
                    <button
                      type="button"
                      data-testid="pg-refresh-meta-btn"
                      className="mq-btn text-[10px]"
                      onClick={refreshExchangeMeta}
                      disabled={metaBusy || !metaExchange}
                    >
                      {metaBusy ? t("saving") : t("pgRefreshMeta")}
                    </button>
                  </div>
                )}
              </div>
              {isPG && (
                <div className="text-[11px] text-[var(--mq-text-3)] mb-3 pl-1">
                  {t("pgSectionNote")}
                </div>
              )}
              {isPG && metaResult && (
                <div
                  data-testid="pg-refresh-meta-result"
                  className="text-[11px] font-mono mb-3 pl-1"
                  style={{ color: metaResult.ok ? "var(--mq-green)" : "var(--mq-red)" }}
                >
                  {metaResult.ok
                    ? `✓ ${t("pgRefreshMetaOk")} — ${metaResult.exchange || ""}`
                    : `✗ ${metaResult.exchange || ""} — ${metaResult.error || metaResult.result?.error || "failed"}`}
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {group.fields.map((f) => (
                  <div key={f.key} className="flex flex-col gap-1">
                    <label className="mq-label flex items-center justify-between">
                      <span data-testid={`cfg-label-${f.key}`}>{f.key}</span>
                      {touchedTop[f.key] && <span style={{ color: "var(--mq-yellow)" }}>●</span>}
                    </label>
                    <FieldInput field={f} value={draft[f.key]} onChange={(v) => setTop(f.key, v)} testid={`cfg-${f.key}`} />
                    {(pgNotes[f.key] || f.note) && (
                      <span className="text-[10px] text-[var(--mq-text-3)]">
                        {pgNotes[f.key] || f.note}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })}

        {/* Per-exchange */}
        <div>
          <div className="mq-label mb-3 border-b pb-2" style={{ borderColor: "var(--mq-border)" }}>
            EXCHANGES — per-exchange enable + credentials
          </div>
          <div className="space-y-3">
            {exchanges.map(([exName, exCfg]) => {
              const meta = EXCHANGE_META[exName] || { label: exName, sig: "—", note: "" };
              const isOpen = expandedEx[exName] ?? exCfg.enabled;
              const dirty = !!touchedEx[exName];
              return (
                <div key={exName} className="border" style={{ borderColor: "var(--mq-border)" }} data-testid={`exchange-section-${exName}`}>
                  <button
                    type="button"
                    onClick={() => setExpandedEx((s) => ({ ...s, [exName]: !isOpen }))}
                    className="w-full flex items-center justify-between px-4 py-3 hover:bg-white/5 transition-colors"
                    data-testid={`exchange-toggle-${exName}`}
                  >
                    <div className="flex items-center gap-3">
                      <span
                        className="mq-status-dot"
                        style={{ background: exCfg.enabled ? "var(--mq-green)" : "var(--mq-text-3)" }}
                      />
                      <span className="mq-heading text-sm font-bold uppercase tracking-wider">{meta.label}</span>
                      <span className="mq-label text-[var(--mq-text-3)]">{meta.sig}</span>
                      {dirty && <span style={{ color: "var(--mq-yellow)" }}>●</span>}
                    </div>
                    <span className="mq-label">{isOpen ? "▲" : "▼"}</span>
                  </button>
                  {isOpen && (
                    <div className="px-4 pb-4">
                      <div className="text-[10px] text-[var(--mq-text-3)] mb-3 pl-1">
                        {meta.note}
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {PER_EX_FIELDS.map((f) => (
                          <div key={f.key} className="flex flex-col gap-1">
                            <label className="mq-label flex items-center justify-between">
                              <span>{f.key}</span>
                              {touchedEx[exName]?.[f.key] && <span style={{ color: "var(--mq-yellow)" }}>●</span>}
                            </label>
                            <FieldInput
                              field={f}
                              value={exCfg[f.key]}
                              onChange={(v) => setEx(exName, f.key, v)}
                              testid={`cfg-ex-${exName}-${f.key}`}
                            />
                            {f.note && <span className="text-[10px] text-[var(--mq-text-3)]">{f.note}</span>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
